import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import Popup from 'components/molecules/popup';
import { Form } from 'components/organisms';
import { FormikHelpers } from 'formik';
import { useCustomParamId } from 'hooks/useParamId';
import { useReadMessages } from 'hooks/useReadMessages';
import { t } from 'i18next';
import { useEffect } from 'react';
import { PiCheck } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useGetMessages,
  useGetThread,
  usePostMessage,
  usePutCloseThread,
} from 'services/chat';
import { ICreateThread, THREAD_STATUS } from 'services/chat/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const Thread = () => {
  const labelId = useCustomParamId('id');
  const threadId = useCustomParamId('threadId');
  const navigate = useNavigate();
  const createMessage = usePostMessage();
  const thread = useGetThread(threadId);
  const messages = useGetMessages({ messageThreadId: threadId });
  const closeThread = usePutCloseThread();
  const queryClient = useQueryClient();

  const onThread = async (
    values: ICreateThread,
    formikHelpers: FormikHelpers<ICreateThread>,
  ) =>
    await createMessage.mutateAsync(
      { threadId, ...values },
      {
        onSuccess: data => {
          formikHelpers.resetForm();
          messages.refetch();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      },
    );

  useReadMessages(messages.data?.pages.flat(), labelId, async () => {
    await queryClient.refetchQueries({
      queryKey: ['chats-list'],
    });

    await queryClient.refetchQueries({
      queryKey: ['chat-unread-count'],
    });
  });

  useEffect(() => {
    messages.refetch();
  }, []);

  return (
    <Form.Support
      status={thread.data?.messageThreadStatus.id}
      labelId={labelId}
      initialValues={{
        subject: thread.data?.subject ?? '',
        message: '',
        attachments: [],
        recipientId: labelId,
      }}
      messages={messages.data?.pages.flat().reverse()}
      isPending={createMessage.isPending}
      isLoading={messages.isLoading}
      isFetchingNextPage={messages.isFetchingNextPage}
      fetchNextPage={messages.fetchNextPage}
      onSubmit={onThread}
      topbarContent={
        thread.data?.messageThreadStatus.id !== THREAD_STATUS.closed ? (
          <Popup.DeleteWarning
            transI18nKey='popup.thread-close'
            name={thread.data?.subject}
            removeLoading={closeThread.isPending}
            onRemove={async actions =>
              await closeThread.mutateAsync(
                {
                  messageThreadId: threadId,
                },
                {
                  onError: error => onErrorShowAllErrorsToast(error),
                  onSuccess: () => {
                    thread.refetch();
                    actions.close();
                  },
                },
              )
            }
            trigger={
              <Button
                variant={BUTTON_VARIANT.submit}
                isLoading={closeThread.isPending}>
                <Button.Icon icon={<PiCheck />} />
                <Button.Text text={t('button.end')} />
              </Button>
            }
          />
        ) : undefined
      }
    />
  );
};

export default Thread;
